import React, { useMemo, useState, useEffect } from 'react';
import { useApiClient } from '../../service/API';
import StageSearch from './StageSearch';
import Pill from './Pill';
import '../../styling/service-flow-manager.css';
import { useNavigate } from 'react-router-dom';
import { TICKET_CLOSED_CODE, TICKET_CLOSED_NAME, isTicketClosed, makeTicketClosed, normalizeWithTicketClosed } from './helpers';
import { getApiErrorMessage } from '../../service/errorHandler';

export default function CreateService() {
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const [serviceType, setServiceType] = useState('');
    const [stages, setStages] = useState([]);

    useEffect(() => {
        setStages((prev) => normalizeWithTicketClosed(prev));
        // run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [notice, setNotice] = useState(null); // {type:'ok'|'err', msg}

    const sortedStages = useMemo(() => stages.map((s, i) => ({ ...s, position: i + 1 })), [stages]);
    const showOk = (msg) => setNotice({type:'ok', msg});
    const showErr = (msg) => setNotice({type:'err', msg});

    // helper to insert just before the Ticket Closed sentinel
    function insertBeforeTicketClosed(prev, item) {
    const idxClosed = prev.findIndex(isTicketClosed);
    if (idxClosed === -1) return [...prev, item]; // fallback, though TC should exist
    const next = [...prev];
    next.splice(idxClosed, 0, item);
    return next;
    }

    function addExistingStage(picked) {
    if (isTicketClosed(picked) && stages.some(isTicketClosed)) {
        showErr('Ticket Closed is already included.');
        return;
    }
    if (stages.some((s) => s.id === picked.id)) {
        showErr('This stage is already in the list.');
        return;
    }
    setStages((prev) =>
        normalizeWithTicketClosed(
        insertBeforeTicketClosed(prev, {
            id: picked.id,
            code: picked.code,
            name: picked.name,
            // new item lands right before Ticket Closed
            position: prev.length, // Ticket Closed shifts to prev.length + 1
        })
        )
    );
    }

    function addNewStage(name) {
    if (
        name.trim().toLowerCase() === TICKET_CLOSED_NAME.toLowerCase() &&
        stages.some(isTicketClosed)
    ) {
        showErr('Ticket Closed is already included.');
        return;
    }
    setStages((prev) =>
        normalizeWithTicketClosed(
        insertBeforeTicketClosed(prev, {
            id: null,
            code: null,
            name,
            position: prev.length, // Ticket Closed shifts to prev.length + 1
        })
        )
    );
    }

    function move(idx, dir) {
        setStages((prev) => {
            const next = [...prev]; const j = idx + dir;
            // prevent moving Ticket Closed
            if (isTicketClosed(next[idx])) return prev;
            // also disallow swapping past the last (which is Ticket Closed)
            if (j < 0 || j >= next.length) return prev;
            if (isTicketClosed(next[j])) return prev;
            [next[idx], next[j]] = [next[j], next[idx]];
            return normalizeWithTicketClosed(next);
        });
    }

    function removeAt(idx) {
        setStages((prev) => {
            if (isTicketClosed(prev[idx])) return prev; // can't remove TC
            const next = prev.filter((_, i) => i !== idx);
            return normalizeWithTicketClosed(next);
        });
    }

    async function save() {
        if (!serviceType.trim()) { showErr('Service name is required.'); return; }
        if (sortedStages.length === 0) { showErr('Add at least one stage.'); return; }
        const enforced = normalizeWithTicketClosed(sortedStages);
        const payload = {
            serviceType: serviceType.trim(),
            stages: enforced.map((s) => ({
                id: s.id ?? undefined,
                name: s.id ? undefined : s.name,
            }))
        };

        try {
            await apiClient.post(`/config/service-flow`, payload);
            showOk('Service created.');
            navigate('/service-flows'); // back to list
        } catch (e) {
            showErr(getApiErrorMessage(e, 'Failed to create service.'));
        }
    }

    return (
        <div className="sfm">
        <div className="sfm-container">
            <header style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
            <div>
                <h1>Create New Service</h1>
                <p>Provide a service name and arrange its stages.</p>
            </div>
            <div style={{display:'flex', gap:8}}>
                <button className="sfm-btn sfm-btn-secondary" onClick={()=>navigate('/service-flows')}>Back</button>
                <button className="sfm-btn sfm-btn-primary" onClick={save}>Add Service</button>
            </div>
            </header>

            {notice && (
            <div className={`sfm-notice ${notice.type}`} role="status">
                <span>{notice.msg}</span>
                <button className="sfm-btn sfm-btn-secondary" onClick={()=>setNotice(null)}>✕</button>
            </div>
            )}

            <section className="sfm-card">
            <label htmlFor="svc">Service Name</label>
            <input id="svc" value={serviceType} onChange={(e)=>setServiceType(e.target.value)} type="text" placeholder="e.g. INCIDENT, RETURN, WARRANTY"/>
            </section>

            <section className="sfm-card">
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                <h2>Stages</h2>
                <Pill>{sortedStages.length} selected</Pill>
            </div>

            <table className="sfm-stage-table">
                <thead>
                <tr>
                    <th style={{width:48}}>#</th>
                    <th style={{width:'20%'}}>Code</th>
                    <th>Name</th>
                    <th style={{width:'15%'}}>Type</th>
                    <th style={{width:'25%', textAlign:'right'}}>Actions</th>
                </tr>
                </thead>
                <tbody>
                {sortedStages.length === 0 ? (
                    <tr><td colSpan={5} style={{padding:16, opacity:0.7}}>No stages yet. Add below.</td></tr>
                ) : (
                    sortedStages.map((s, idx)=>(
                    <tr key={`${s.id ?? 'new'}-${idx}`}>
                        <td>{idx+1}</td>
                        <td>{s.code ?? <span style={{opacity:0.6}}>(new)</span>}</td>
                        <td>{s.name}</td>
                        <td>
                        <button className={`sfm-btn sfm-btn-secondary`} onClick={()=>{}} disabled>
                            Stage
                        </button>
                        </td>
                        <td style={{textAlign:'right'}}>
                        <div style={{display:'inline-flex', gap:6}}>
                            <button className="sfm-btn sfm-btn-secondary" onClick={()=>move(idx,-1)}
                            disabled={idx===0 || isTicketClosed(s)}>▲</button>
                            <button className="sfm-btn sfm-btn-secondary" onClick={()=>move(idx,1)}
                            disabled={idx===sortedStages.length-2 || isTicketClosed(s)}>▼</button>
                            <button className="sfm-btn sfm-btn-destructive" onClick={()=>removeAt(idx)}
                            disabled={isTicketClosed(s)}>🗑</button>
                        </div>
                        </td>
                    </tr>
                    ))
                )}
                </tbody>
            </table>

            <div style={{marginTop:18}}>
                <h3 style={{fontSize:16, fontWeight:600, marginBottom:8}}>Add Stages</h3>
                <StageSearch onPick={addExistingStage} onCreateNew={addNewStage} />
                <p style={{fontSize:12, marginTop:8}}>
                Only <b>Booking Creation</b> and <b>Ticket Closed</b> can be terminal when saved.
                </p>
            </div>
            </section>
        </div>
        </div>
    );
}
